<template>
  <div class="chat-outer">
    <audio ref="audioElement" autoplay controls style="display: none;"></audio>

    <!-- Show chat start if not in session and not in voice -->
    <div v-if="!sessionId && !showVoiceSession" class="start-chat start-chat-outline">
      <select v-model="systemMessageFile" class="start-chat-select" v-if="!dynamicValues">
        <option value="BusinessSetup">BusinessSetup</option>
        <option value="DynamicFreelancer">DynamicFreelancer</option>
        <option value="NathanDigital">NathanDigital</option>
        <option value="HRMS">HRMS</option>
      </select>
      <button @click="startChat" :disabled="loading">Start Chat</button>
      <button @click="onTalkToAgent" class="voice-btn">🎤 Talk to Agent</button>
    </div>

    <!-- Chat UI -->
    <div v-else-if="sessionId && !showVoiceSession" class="chat-container">
      <div class="chat-header">
        <div class="avatar"></div>
        <div class="chat-title">
          <div class="name">Nathan AI</div>
          <div class="status">
            <span class="online-dot"></span>
            Online
          </div>
        </div>
      </div>
      <div class="chat-history" ref="chatHistory">
        <div v-for="(msg, idx) in messages" :key="idx" :class="['chat-bubble', msg.role]">
          <span class="content">{{ msg.content }}</span>
        </div>
        <div v-if="loading" class="chat-bubble assistant typing">
          <span class="content typing-indicator">
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          </span>
        </div>
      </div>
      <form class="chat-input-row" @submit.prevent="sendQuestion">
        <div class="textarea-emoji-wrap">
          <textarea v-model="question" placeholder="Type your message..." required :disabled="loading"
            @keydown.enter.exact.prevent="sendQuestion" @keydown.enter.shift.stop></textarea>
          <button type="button" class="emoji-btn" @click="showEmojiPicker = !showEmojiPicker" title="Add Emoji"
            tabindex="-1">😊</button>
          <div v-if="showEmojiPicker" class="emoji-picker-popover-inside">
            <emoji-picker @emoji-click="addEmoji"></emoji-picker>
          </div>
        </div>
        <button type="submit" :disabled="loading || !question" class="send-btn">
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path d="M3 20v-6l13-2-13-2V4l18 8-18 8z" fill="#fff" />
          </svg>
        </button>
        <button type="button" @click="endChat" :disabled="loading" class="end-btn" title="End Chat">
          ✕
        </button>
      </form>
      <div v-if="error" class="error">{{ error }}</div>
      <!-- <div v-if="sessionId" class="session-id">Session: {{ sessionId }}</div> -->
    </div>

    <!-- Voice session UI -->
    <div v-else-if="showVoiceSession" class="voice-session-container">
      <div class="voice-session-header">
        <span style="color: #fff; text-shadow: 0 2px 8px #6c74e6;">🎤 Nathan AI</span>
        <button @click="closeVoiceSession" class="close-btn">✕</button>
      </div>
      <div class="voice-session-body">
        <div v-if="!isSessionActive">
          <span>Connecting...</span>
        </div>
        <div v-else>

          <img src="@/assets/loading.gif" alt="Loading..." class="loading-gif-no-bg" :class="{ 'pulse': pulseLoading }"
            :style="{ animationDuration: pulseLoading ? pulseDuration : undefined, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }"
            @animationend="pulseLoading = false" />
          <!-- <div class="event-log">
            <div v-for="(event, idx) in events" :key="idx">
              <pre>{{ event }}</pre>
            </div>
          </div> -->
          <!-- <button @click="stopVoiceSession" class="stop-voice-btn">Stop Session</button> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import { ref, reactive, nextTick } from 'vue';

export default {
  name: 'ChatBox',
  data() {
    return {
      toolsArray: [],
      systemURL: "",
      systemAuthConfig: null,
      dynamicValues: false,
      pulseDuration: '2s',
      pulseInterval: null, // Store the interval ID for pulse animation
      pulseLoading: true,
      question: '',
      systemMessageFile: 'BusinessSetup',
      answer: '',
      error: '',
      loading: false,
      sessionId: null,
      messages: [],
      showEmojiPicker: false,
      showVoiceSession: false,
      isSessionActive: false,
      events: [],
      dataChannel: null,
      peerConnection: null,
      audioElement: null,
      userID: "65d091b6fa762b988d4966b8", // Example user ID, replace with actual user ID logic
      voiceSessions: {}, // Store voice sessions by sessionId
    };
  },
  mounted() {
    // Check for URL params for systemMessageFile and userID
    const urlParams = new URLSearchParams(window.location.search);
    const systemMessageFile = urlParams.get('systemMessageFile');
    const userID = urlParams.get('userID');
    if (systemMessageFile) {
      this.systemMessageFile = systemMessageFile;
      this.dynamicValues = true;
    }
    if (userID) {
      this.userID = userID;
      this.dynamicValues = true;
    }
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatHistory;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    generateSessionId(type) {
      return 'sess_' + type + '_' + Math.random().toString(36).substr(2, 9);
    },
    async startChat() {
      this.sessionId = this.generateSessionId('ct');
      this.answer = '';
      this.error = '';
      this.messages = [];
    },
    async sendQuestion() {
      if (!this.sessionId || !this.question.trim()) {
        this.error = 'No active session. Please start a chat.';
        return;
      }
      const userMsg = this.question;
      this.messages.push({ role: 'user', content: userMsg });
      this.scrollToBottom();
      this.question = ''; // Clear textbox immediately
      this.loading = true;
      this.answer = '';
      this.error = '';
      try {
        const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question: userMsg,
            userID: this.userID,
            sessionId: this.sessionId,
            systemMessageFile: this.systemMessageFile || undefined
          })
        });
        const data = await res.json();
        if (res.ok) {
          this.answer = data.answer;
          // Add assistant message to history
          this.messages.push({ role: 'assistant', content: data.answer });
          this.scrollToBottom();
        } else {
          this.error = data.error || 'Error from server';
        }
      } catch (e) {
        this.error = 'Network error';
      }
      this.loading = false;
    },
    async endChat() {
      if (!this.sessionId) return;
      this.loading = true;
      this.error = '';
      try {
        const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/chat/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId })
        });
        const data = await res.json();
        if (res.ok) {
          this.sessionId = null;
          this.answer = '';
          this.question = '';
          this.systemMessageFile = 'BusinessSetup';
          this.messages = [];
        } else {
          this.error = data.error || 'Error ending chat';
        }
      } catch (e) {
        this.error = 'Network error';
      }
      this.loading = false;
    },
    addEmoji(event) {
      this.question += event.detail.unicode;
      this.showEmojiPicker = false;
    },
    async startVoiceSession() {
      // Get a session token for OpenAI Realtime API
      const tokenResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/token`, {
        method: 'POST',
        body: JSON.stringify({ systemMessageFile: this.systemMessageFile, userID: this.userID }),
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await tokenResponse.json();
      const EPHEMERAL_KEY = data.client_secret.value;
      this.systemURL = data.systemURL ? data.systemURL : "";
      this.systemAuthConfig = data.systemAuthConfig ? data.systemAuthConfig : null;
      this.toolsArray = data.toolsArray ? data.toolsArray : [];
      // Create a peer connection
      this.peerConnection = new RTCPeerConnection();

      // Set up to play remote audio from the model
      this.audioElement = this.$refs.audioElement;
      this.audioElement.autoplay = true;
      this.peerConnection.ontrack = (e) => (this.audioElement.srcObject = e.streams[0]);

      // Add local audio track for microphone input in the browser
      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.peerConnection.addTrack(ms.getTracks()[0]);

      // Set up data channel for sending and receiving events
      this.dataChannel = this.peerConnection.createDataChannel('oai-events');

      // Start the session using the Session Description Protocol (SDP)
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Compose instructions (hardcoded for now)
      const instructions = `You are a sales agent for Dynamic Freelancer...`; // (use your full instructions here)
      function base64EncodeUnicode(str) {
        return btoa(unescape(encodeURIComponent(str)));
      }
      const originalSdp = offer.sdp;
      const modifiedSdp = originalSdp + `\r\na=system_message:${base64EncodeUnicode(instructions)}\r\n`;
      this.sessionId = this.generateSessionId('vc');
      this.voiceSessions[this.sessionId] = []; // Initialize voice session for this sessionId
      const sdpResponse = await fetch(`${process.env.VUE_APP_API_BASE_URL}/voice`, {
        method: "POST",
        body: JSON.stringify({
          modifiedSdp: modifiedSdp,
          sessionId: this.sessionId,
          EPHEMERAL_KEY: EPHEMERAL_KEY,
          systemMessageFile: this.systemMessageFile || undefined,
        }),
        headers: { 'Content-Type': 'application/json' }
      });

      const answer = {
        type: "answer",
        sdp: await sdpResponse.text(),
      };
      await this.peerConnection.setRemoteDescription(answer);


      // Listen for data channel events
      this.dataChannel.addEventListener("message", (e) => {
        const event = JSON.parse(e.data);
        if (event.type === 'conversation.item.input_audio_transcription.completed') {
          this.voiceSessions[this.sessionId].push({
            role: 'user',
            content: event.transcript
          });

          this.createLogWithSession(
            {
              role: 'user', content: event.transcript, timestamp: new Date(), usage: {
                promptTokens: 0,
                completionTokens: 0,
                totalTokens: 0
              }
            },
            this.sessionId,
            this.systemMessageFile || 'HRMS',
            this.voiceSessions[this.sessionId],
            this.userID || 'unknown_user',
            'voice'
          );
        }
        if (event.type === 'response.audio_transcript.done') {
          this.voiceSessions[this.sessionId].push({
            role: 'assistant',
            content: event.transcript
          });
          this.createLogWithSession(
            {
              role: 'assistant', content: event.transcript, timestamp: new Date(), usage: {
                promptTokens: 0,
                completionTokens: 0,
                totalTokens: 0
              }
            },
            this.sessionId,
            this.systemMessageFile || 'HRMS',
            this.voiceSessions[this.sessionId],
            this.userID || 'unknown_user',
            'voice'
          );
        }
        if (event.type === `response.function_call_arguments.done`) {
          this.callAPIArguments(
            event,
            this.systemURL,
            this.systemAuthConfig,
            event.arguments,
            event.name,
            this.toolsArray
          )
        }
        if (!event.timestamp) {
          event.timestamp = new Date().toLocaleTimeString();
        }
        if (event.type.startsWith('input_audio_buffer')) {
          if (this.pulseInterval) clearInterval(this.pulseInterval);
          this.pulseDuration = '2s'; // Reset pulse duration for new audio input
          this.pulseLoading = true; // Start pulse animation
        }
        else if (event.type == 'output_audio_buffer.stopped') {
          if (this.pulseInterval) clearInterval(this.pulseInterval);
          this.pulseDuration = '2s'; // Reset pulse duration for new audio input
          this.pulseLoading = true; // Start pulse animation
        }
        else if (event.type.startsWith('response')) {
          // Change pulse duration every 500ms for a more dynamic effect
          if (this.pulseInterval) clearInterval(this.pulseInterval);
          this.pulseInterval = setInterval(() => {
            let randomDuration = Math.random() * (0.6 - 0.4) + 0.4; // Random duration between 0.4s and 0.6s
            this.pulseDuration = `${randomDuration.toFixed(2)}s`;
          }, 300);
          this.pulseLoading = true; // Start pulse animation
        }
      });

      this.dataChannel.addEventListener("open", () => {
        this.isSessionActive = true;
        this.events = [];
        this.sendTextMessage(`Hi`, 'user');
        // this.$nextTick(() => {
        //   this.audioElement = this.$refs.audioElement;
        //   if (this.audioElement) {
        //     this.audioElement.autoplay = true;
        //     this.peerConnection.ontrack = (e) => {
        //       console.log('Tracks:', e.streams[0].getAudioTracks());
        //       if (this.audioElement) {
        //         this.audioElement.srcObject = e.streams[0];
        //         console.log(this.audioElement, 'fdsafds');
        //         this.audioElement.play().catch(console.warn);
        //       }
        //     };
        //   }
        // });
      });
    },
    stopVoiceSession() {
      if (this.dataChannel) {
        this.dataChannel.close();
      }
      if (this.peerConnection) {
        this.peerConnection.getSenders().forEach((sender) => {
          if (sender.track) sender.track.stop();
        });
        this.peerConnection.close();
      }
      this.isSessionActive = false;
      this.dataChannel = null;
      this.peerConnection = null;
    },
    closeVoiceSession() {
      this.createLogSummery(this.sessionId);
      this.voiceSessions = {}; // Clear voice sessions
      this.sessionId = null; // Reset session ID
      this.loading = false;
      this.sessionId = null;
      this.pulseLoading = false;

      // Clear pulse interval
      if (this.pulseInterval) clearInterval(this.pulseInterval);
      this.pulseInterval = null;

      // Clean up events and messages
      this.events = [];
      this.messages = [];
      this.question = '';
      this.answer = '';
      this.error = '';

      // Clean up audio element
      if (this.audioElement) {
        this.audioElement.srcObject = null;
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
      }

      // Clean up peer connection and data channel
      if (this.dataChannel) {
        this.dataChannel.close();
        this.dataChannel = null;
      }
      if (this.peerConnection) {
        this.peerConnection.getSenders().forEach(sender => {
          if (sender.track) sender.track.stop();
        });
        this.peerConnection.close();
        this.peerConnection = null;
      }

      this.isSessionActive = false;
      this.showVoiceSession = false;
    },
    sendClientEvent(message) {
      if (this.dataChannel) {
        const timestamp = new Date().toLocaleTimeString();
        message.event_id = message.event_id || crypto.randomUUID();
        this.dataChannel.send(JSON.stringify(message));
        if (!message.timestamp) {
          message.timestamp = timestamp;
        }
        this.events.unshift(message);
      }
    },
    sendTextMessage(message, role = 'user') {
      const event = {
        type: "conversation.item.create",
        item: {
          type: "message",
          role: role,
          content: [
            {
              type: "input_text",
              text: message,
            },
          ],
        },
      };
      this.sendClientEvent(event);
      this.sendClientEvent({ type: "response.create" });
    },
    onTalkToAgent() {
      this.showVoiceSession = true;
      this.isSessionActive = false;
      this.startVoiceSession();
    },
    async createLogWithSession(message, sessionId, system, meta, user, type) {
      // This function should handle creating or updating logs with the provided parameters
      // Implement your logging logic here
      await fetch(`${process.env.VUE_APP_API_BASE_URL}/logs/createOrUpdateLogWithSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          sessionId,
          system,
          meta,
          user,
          type,
          client: this.systemMessageFile || 'Undefined'
        })
      });
    },
    async createLogSummery(sessionId) {
      await fetch(`${process.env.VUE_APP_API_BASE_URL}/logs/summery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId
        })
      });
    },
    async callAPIArguments(event, systemURL, systemAuthConfig, args, name, toolsArray) {
      try {
        let res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/functions/executecallAPIArguments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            {
              "url": systemURL,
              "authConfig": systemAuthConfig,
              "args": args,
              "toolName": name,
              "functionName": "callAPIArguments",
              "toolsArray": toolsArray
            }
          )
        })
        let response = await res.json();
        this.dataChannel.send(JSON.stringify(
          {
            type: "conversation.item.create",
            item:
            {
              type: "function_call_output",
              call_id: event.call_id,
              output: JSON.stringify(response)
            }
          }
        ));
        this.dataChannel.send(JSON.stringify({ type: 'response.create' }));
      }
      catch (error) {
        this.dataChannel.send(JSON.stringify(
          {
            type: "conversation.item.create",
            item:
            {
              type: "function_call_output",
              call_id: event.call_id,
              output: "Error calling function:" + error.message || "Unknown error"
            }
          }
        ));
        this.dataChannel.send(JSON.stringify({ type: 'response.create' }));
      }
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Figtree:wght@400;600&display=swap');

.chat-outer,
.chat-container,
.chat-header,
.chat-title,
.chat-history,
.chat-bubble,
.chat-input-row,
.start-chat,
.start-chat-select,
button,
textarea,
select {
  font-family: 'Figtree', sans-serif;
}

.chat-outer {
  min-height: 100vh;
  background: #ffffff;
  /* Soft grey-pink */
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-container {
  width: 370px;
  height: 520px;
  /* Fixed height */
  background: #4f5bd5;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(44, 62, 80, 0.2);
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;
}

.chat-header {
  display: flex;
  align-items: center;
  padding: 0.8rem 1rem 0.3rem 1rem;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff url('https://internalerp.nathandigital.com/_nuxt/bot6.u0bwgGHA.gif') center/cover no-repeat;
  margin-right: 0.7rem;
  border: 2px solid #fff;
}

.chat-title .name {
  color: #fff;
  font-weight: bold;
  font-size: 1rem;
}

.chat-title .status {
  color: #b2b8ff;
  font-size: 0.85rem;
}

.online-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #2ecc40;
  border-radius: 50%;
  margin-right: 4px;
  box-shadow: 0 0 3px #2ecc40;
  vertical-align: middle;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 0.7rem 1rem 0.7rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  scrollbar-width: thin;
  scrollbar-color: #b2b8ff #e9e9f6;
}

.chat-history::-webkit-scrollbar {
  width: 8px;
  border-radius: 8px;
  background: #e9e9f6;
}

.chat-history::-webkit-scrollbar-thumb {
  background: #b2b8ff;
  border-radius: 8px;
}

.chat-history::-webkit-scrollbar-thumb:hover {
  background: #6c74e6;
}

.chat-bubble {
  max-width: 95%;
  min-width: 60px;
  padding: 0.5rem 0.9rem;
  border-radius: 16px;
  font-size: 0.97rem;
  margin-bottom: 0.15rem;
  word-break: break-word;
  white-space: pre-line;
  display: inline-block;
  overflow-wrap: break-word;
}

.chat-bubble.user {
  align-self: flex-end;
  background: #fff;
  color: #222;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 16px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.chat-bubble.assistant {
  align-self: flex-start;
  background: #6c74e6;
  color: #fff;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 16px;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.typing-indicator {
  display: flex;
  align-items: center;
  height: 18px;
}

.dot {
  height: 7px;
  width: 7px;
  margin: 0 1.5px;
  background-color: #fff;
  opacity: 0.7;
  border-radius: 50%;
  display: inline-block;
  animation: typing 1.4s infinite both;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {

  0%,
  80%,
  100% {
    opacity: 0.2;
  }

  40% {
    opacity: 1;
  }
}

.chat-input-row {
  display: flex;
  align-items: center;
  padding: 0.7rem 1rem 1rem 1rem;
  background: transparent;
  border-radius: 0 0 24px 24px;
  gap: 0.4rem;
  position: relative;
}

.textarea-emoji-wrap {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.chat-input-row textarea {
  width: 100%;
  border: none;
  border-radius: 14px;
  padding: 0.5rem 2.5rem 0.5rem 0.9rem;
  /* right padding for emoji button */
  font-size: 0.97rem;
  resize: none;
  outline: none;
  background: #fff;
  color: #222;
  min-height: 36px;
  max-height: 70px;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.04);
}

.emoji-btn {
  position: absolute;
  right: 10px;
  bottom: 8px;
  background: transparent;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 2;
  padding: 0;
}

.emoji-btn:hover {
  background: #f8e9f0;
}

.send-btn {
  background: #6c74e6;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.2rem;
  cursor: pointer;
  transition: background 0.2s;
}

.send-btn:disabled {
  background: #b2b8ff;
  cursor: not-allowed;
}

.send-btn svg {
  width: 18px;
  height: 18px;
}

.end-btn {
  background: #ff4d4f;
  border: none;
  color: #fff;
  font-size: 1.1rem;
  margin-left: 0.1rem;
  cursor: pointer;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: background 0.2s, box-shadow 0.2s;
  position: relative;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.08);
}

.end-btn:disabled {
  background: #ffbaba;
  color: #fff;
  cursor: not-allowed;
}

.end-btn:hover:not(:disabled) {
  background: #d8000c;
  color: #fff;
}

/* Optional: Custom tooltip styling for accessibility */
.end-btn[title]:hover::after {
  content: attr(title);
  position: absolute;
  left: 50%;
  bottom: 120%;
  transform: translateX(-50%);
  background: #222;
  color: #fff;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.85em;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0.95;
  z-index: 10;
}

.error {
  color: #ffbaba;
  background: #d8000c;
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  margin: 0.4rem 1rem 0 1rem;
  font-size: 0.93em;
}

.session-id {
  margin: 0.3rem 1rem 0.7rem 1rem;
  font-size: 0.8em;
  color: #b2b8ff;
  text-align: right;
}

.start-chat {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.start-chat input {
  border-radius: 14px;
  border: none;
  padding: 0.5rem 0.9rem;
  font-size: 0.97rem;
  margin-bottom: 0.5rem;
  width: 250px;
}

.start-chat button {
  background: #6c74e6;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 0.5rem 1.2rem;
  font-size: 0.97rem;
  cursor: pointer;
  transition: background 0.2s;
}

.start-chat button:disabled {
  background: #b2b8ff;
  cursor: not-allowed;
}

.start-chat-outline {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.08), 0 0 0 2px #6c74e6;
  padding: 2rem 2rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.start-chat-select {
  border: 1.5px solid #6c74e6;
  border-radius: 14px;
  padding: 0.6rem 1.2rem;
  font-size: 1rem;
  background: #f8f8ff;
  color: #222;
  outline: none;
  width: 220px;
  margin-bottom: 0.5rem;
  transition: border 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.04);
}

.start-chat-select:focus {
  border-color: #4f5bd5;
  box-shadow: 0 0 0 2px #b2b8ff;
}

.emoji-picker-popover {
  position: absolute;
  bottom: 60px;
  left: 20px;
  z-index: 100;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.18);
  border-radius: 12px;
  overflow: hidden;
}

.emoji-picker-popover-inside {
  position: absolute;
  bottom: 40px;
  right: 0;
  z-index: 100;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.18);
  border-radius: 12px;
  overflow: hidden;
}

.voice-session-container {
  background: #000000;
  border-radius: 18px;
  box-shadow: 0 4px 24px rgba(44, 62, 80, 0.08), 0 0 0 2px #6c74e6;
  padding: 1.5rem;
  width: 370px;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
}

.voice-session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  margin-bottom: 1rem;
}

.close-btn {
  background: #ff4d4f;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  cursor: pointer;
}

.voice-session-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.start-voice-btn,
.stop-voice-btn {
  background: #6c74e6;
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 0.5rem 1.2rem;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
}

.start-voice-btn:disabled,
.stop-voice-btn:disabled {
  background: #b2b8ff;
  cursor: not-allowed;
}

.event-log {
  max-height: 120px;
  overflow-y: auto;
  background: #f8f8ff;
  border-radius: 8px;
  margin: 1rem 0;
  padding: 0.5rem;
  font-size: 0.9em;
}

.voice-btn {
  background: #fff;
  color: #6c74e6;
  border: 1.5px solid #6c74e6;
  border-radius: 14px;
  padding: 0.5rem 1.2rem;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.2s, color 0.2s;
}

.voice-btn:hover {
  background: #6c74e6;
  color: #fff;
}

.transcription-box {
  background: #f8f8ff;
  border-radius: 10px;
  padding: 0.7rem 1rem;
  margin-bottom: 0.7rem;
  font-size: 1.08em;
  color: #222;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.06);
  min-height: 32px;
  display: flex;
  align-items: center;
  gap: 0.5em;
  word-break: break-word;
}

.transcription-label {
  color: #6c74e6;
  font-weight: 600;
  margin-right: 0.5em;
}

.transcription-text {
  font-family: 'Figtree', sans-serif;
  font-size: 1.08em;
}

.loading-gif-no-bg {
  display: block;
  margin: 0 auto;
  width: 38px;
  height: 38px;
  background: transparent;
  object-fit: contain;
  /* Optional: add a subtle drop shadow for visibility */
  filter: drop-shadow(0 2px 8px rgba(44, 62, 80, 0.08));
}

.pulse {
  animation: pulse 1.2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.6;
    transform: scale(1.08);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>